/**
 * EngagementHeader — Phase 6a.10 / Spec §21.7.1.
 *
 * Two-row header rendered above `<ContractFormContainer>` on the contract
 * entry page:
 *
 *   Row 1: {clientName} | {auditPeriodStart} – {auditPeriodEnd} | Functional: {ccy}
 *          "Sözleşme N / M"                              [Önceki] [Sonraki]
 *   Row 2: ● 1  ● 2  ◐ 3  ○ 4 …  (status dots, current contract highlighted)
 *
 * The status row scrolls horizontally on narrow viewports.
 */

import { ChevronLeft, ChevronRight, List } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { formatIsoToDisplay } from "@/components/ui/date-picker.utils";
import { Separator } from "@/components/ui/separator";
import type { EngagementHistoryEntry } from "@/types";

import { ContractStatusDot } from "./ContractStatusDot";

interface EngagementHeaderProps {
  engagement: EngagementHistoryEntry;
  /** 0-based index of the contract currently being edited. */
  currentContractIdx: number;
  /**
   * Phase 6a.23 polish — Finding AH. The page computes these from
   * `findAdjacentLiveContractIndex` so the header's Prev/Next buttons
   * skip soft-deleted slots in lock-step with the footer mirror. A
   * `null` value means the corresponding boundary is reached (no live
   * slot remaining in that direction).
   */
  prevLiveIdx: number | null;
  nextLiveIdx: number | null;
  onNavigate: (idx: number) => void;
  onReviewList: () => void;
}

export function EngagementHeader({
  engagement,
  currentContractIdx,
  prevLiveIdx,
  nextLiveIdx,
  onNavigate,
  onReviewList,
}: EngagementHeaderProps) {
  const { t } = useTranslation();

  // `total` is the array length (incl. soft-deleted slots) so the
  // "Sözleşme N / M" indicator preserves the original slot numbering
  // even when some slots are in their soft-delete state — that's the
  // documented UX (slot identity is stable; deletion reserves the
  // position).
  const total = engagement.contracts.length;
  const canGoPrev = prevLiveIdx !== null;
  const canGoNext = nextLiveIdx !== null;

  return (
    <div
      data-testid="engagement-header"
      className="rounded-lg border bg-card shadow-sm"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 p-4">
        <div className="space-y-1">
          <div className="text-base font-semibold">
            {engagement.clientName}
          </div>
          <div className="text-sm text-muted-foreground">
            {formatIsoToDisplay(engagement.auditPeriodStart)} —{" "}
            {formatIsoToDisplay(engagement.auditPeriodEnd)}
            <span className="mx-2">·</span>
            <span className="font-medium text-foreground">
              {engagement.functionalCurrency}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="text-sm font-medium tabular-nums"
            data-testid="engagement-header-indicator"
          >
            {t("engagement.contract.header.indicator", {
              current: currentContractIdx + 1,
              total,
            })}
          </div>
          <Separator orientation="vertical" className="h-6 mx-1" />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!canGoPrev}
            onClick={() => {
              if (prevLiveIdx !== null) onNavigate(prevLiveIdx);
            }}
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            {t("engagement.contract.navigation.previous")}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!canGoNext}
            onClick={() => {
              if (nextLiveIdx !== null) onNavigate(nextLiveIdx);
            }}
          >
            {t("engagement.contract.navigation.next")}
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onReviewList}
          >
            <List className="h-4 w-4 mr-1" />
            {t("engagement.contract.navigation.reviewList")}
          </Button>
        </div>
      </div>
      <Separator />
      <div
        className="flex flex-nowrap items-center gap-1 overflow-x-auto px-4 py-2"
        data-testid="engagement-header-status-row"
      >
        {engagement.contracts.map((c, idx) => (
          <ContractStatusDot
            key={idx}
            contractIndex={idx + 1}
            contract={c ?? null}
            isDirty={engagement.contractsDirty?.[idx] ?? false}
            isCurrent={idx === currentContractIdx}
            serverRejection={engagement.lastCalculateError?.[idx]}
            auditPeriod={{
              start: engagement.auditPeriodStart,
              end: engagement.auditPeriodEnd,
            }}
            functionalCurrency={engagement.functionalCurrency}
            onClick={onNavigate}
          />
        ))}
      </div>
    </div>
  );
}
